import { Card, CardContent, List, ListItem, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import * as React from "react";
import { fetchData } from "../../common/services/ApiClient";
import { ApiBaseUrl } from "../../config";
import { createHtmlId } from "../../common/services/Utilities";
import { KeyValue } from "../../interfaces";

const PREFIX = "BankFindDataViz";

const classes = {
  container: `${PREFIX}-container`,
  dataAsOf: `${PREFIX}-dataAsOf`,
  dataVizCard: `${PREFIX}-dataVizCard`,
  title: `${PREFIX}-title`,
  total: `${PREFIX}-total`,
  additionalInfo: `${PREFIX}-additionalInfo`,
  dataVizContent: `${PREFIX}-dataVizContent`,
};

const Root = styled("div")(({ theme }) => ({
  [`& .${classes.container}`]: {
    "padding": 0,
    "& li": {
      display: "block",
      padding: 0,
      marginBottom: "2rem",
    },
  },

  [`& .${classes.dataAsOf}`]: {
    color: theme.palette.primary.main,
    marginTop: ".5rem",
    fontSize: ".875rem",
    lineHeight: ".875rem",
    fontFamily: "'Source Sans Pro'",
  },

  [`& .${classes.dataVizCard}`]: {
    boxShadow: "none",
    backgroundColor: theme.palette.primary.main,
    padding: ".5rem",
  },

  [`& .${classes.title}`]: {
    color: "white",
    textAlign: "center",
    lineHeight: "1rem",
    marginBottom: "1rem",
    paddingBottom: 0,
    fontSize: "18px",
  },

  [`& .${classes.total}`]: {
    fontFamily: "'Source Sans Pro'",
    fontWeight: "bold",
    fontSize: "3rem",
    color: "white",
    textAlign: "center",
    lineHeight: "3rem",
    marginBottom: "1.5rem",
  },

  [`& .${classes.additionalInfo}`]: {
    color: "white",
    textAlign: "center",
    fontWeight: 700,
    fontSize: "1rem",
    lineHeight: ".875rem",
  },

  [`& .${classes.dataVizContent}`]: {
    "padding": 0,
    "&:last-child": {
      paddingBottom: 0,
    },
    "& >h4#total-assets": {
      marginBottom: "1rem",
    },
    "& >p#total-assets-total": {
      marginBottom: "1rem",
    },
    "& >p#total-assets-additional-info": {
      marginBottom: ".5rem",
    },
    "& >h4#total-deposits": {
      marginBottom: "1rem",
    },
    "& >p#total-deposits-total": {
      marginBottom: "1rem",
    },
    "& >p#total-deposits-additional-info": {
      marginBottom: ".5rem",
    },
  },
}));

export default function DataViz() {
  const [vizData, setVizData] = React.useState<KeyValue>({
    total_insured_institutions: {
      title: "Total Insured Institutions",
      total: "",
      additionalInfo: "",
      dataAsOf: "",
    },
    total_insured_branches: {
      title: "Total Insured Branch Offices",
      total: "",
      additionalInfo: "",
      dataAsOf: "",
      style: { backgroundColor: "#547CB7" },
    },
    total_asset: {
      title: "Total Assets",
      total: "",
      additionalInfo: "Millions $USD",
      dataAsOf: "",
    },
    total_deposits: {
      title: "Total Deposits",
      total: "",
      additionalInfo: "Millions $USD",
      dataAsOf: "",
      style: { backgroundColor: "#547CB7" },
    },
  });

  async function getMaxRepDate() {
    const reportDates = await fetchData(
      `${ApiBaseUrl()}/financials?fields=REPDTE&limit=1&sort_by=REPDTE&sort_order=desc`,
    );
    return reportDates.data[0].REPDTE || "99991231";
  }

  React.useEffect(() => {
    function getMillionsValue(value: number) {
      return Math.round(value / 1e3);
    }

    function formatStringDate(date: string) {
      return `${date?.slice(4, 4 + 2)}/${date?.slice(6, 6 + 2)}/${date?.slice(
        0,
        4,
      )}`;
    }
    let isMounted = true;
    (async () => {
      const financialQueryParams = `limit=1&filters=!(BKCLASS:NC OR BKCLASS:NS OR BKCLASS:CU) AND (INSCOML:1 OR INSSAVE:1 OR IBA:1) AND REPDTE:${await getMaxRepDate()} AND ACTIVE:1&total_fields=DEP,ASSET`;

      Promise.all([
        fetchData(`${ApiBaseUrl()}/institutions?filters=ACTIVE:1&limit=0`),
        fetchData(`${ApiBaseUrl()}/locations?limit=0`),
        fetchData(`${ApiBaseUrl()}/financials?${financialQueryParams}`),
      ])
        .then((responses) => {
          if (isMounted) {
            setVizData((prevState) => {
              const newVizData = { ...prevState };

              newVizData.total_insured_institutions.total =
                responses[0].totals.count.toLocaleString();
              newVizData.total_insured_institutions.dataAsOf = `Data as of ${new Date(
                responses[0].createTimestamp,
              ).toLocaleDateString()}, updated weekly.`;

              newVizData.total_insured_branches.total =
                responses[1].totals.count.toLocaleString();
              newVizData.total_insured_branches.dataAsOf = `Data as of ${new Date(
                responses[1].createTimestamp,
              ).toLocaleDateString()}, updated weekly.`;
              newVizData.total_asset.total = `$${getMillionsValue(
                responses[2].totals.ASSET,
              ).toLocaleString()}`;
              newVizData.total_asset.dataAsOf = `Data as of ${new Date(
                responses[2].data[0]
                  ? formatStringDate(responses[2].data[0].RISDATE)
                  : "12/31/9999",
              ).toLocaleDateString()}, updated quarterly.`;

              newVizData.total_deposits.total = `$${getMillionsValue(
                responses[2].totals.DEP,
              ).toLocaleString()}`;
              newVizData.total_deposits.dataAsOf = `Data as of ${new Date(
                responses[2].data[0]
                  ? formatStringDate(responses[2].data[0].RISDATE)
                  : "12/31/9999",
              ).toLocaleDateString()}, updated quarterly.`;

              return newVizData;
            });
          }
        })
        .catch((error) => console.error(error))
        .finally(() => {
          isMounted = false;
        });
    })();
  }, []);

  return (
    <Root id="key-statistics">
      <Typography variant="h2" style={{ paddingBottom: "0px" }}>
        National Key Statistics
      </Typography>
      <Typography
        variant="h4"
        id={"subtitle-keys-stats"}
        style={{ paddingTop: "0px", fontWeight: "normal" }}
      >
        Of Active Institutions
      </Typography>
      {
        <List className={classes.container}>
          {Object.keys(vizData).map((key: string, index: number) => (
            <ListItem key={index}>
              <Card className={classes.dataVizCard} style={vizData[key].style}>
                <CardContent className={classes.dataVizContent}>
                  <Typography
                    variant="h4"
                    id={createHtmlId(vizData[key].title)}
                    className={classes.title}
                  >
                    {vizData[key].title}
                  </Typography>
                  <Typography
                    id={`${createHtmlId(vizData[key].title)}-total`}
                    className={classes.total}
                  >
                    {vizData[key].total}
                  </Typography>
                  <Typography
                    id={`${createHtmlId(vizData[key].title)}-additional-info`}
                    className={classes.additionalInfo}
                  >
                    {vizData[key].additionalInfo}
                  </Typography>
                </CardContent>
              </Card>
              <Typography
                id={`${createHtmlId(vizData[key].title)}-date`}
                className={classes.dataAsOf}
              >
                {vizData[key].dataAsOf}
              </Typography>
            </ListItem>
          ))}
        </List>
      }
    </Root>
  );
}
